import {
  CommissionAgreement,
  CommissionLine,
  CommissionSplit,
  LineOfBusiness,
  MemberStatus,
  TreeMember,
} from '../models/domain.models';
import { addDays } from './rng';
import { COMPANY } from './seed-named';

export const LINE_RATE: Record<LineOfBusiness, number> = {
  auto: 12, home: 15, commerce: 14, liability: 13, 'personal-accident': 18, life: 20,
};
const COMPANY_ADJUST: Record<string, number> = {
  [COMPANY.plata]: 0, [COMPANY.austral]: 1, [COMPANY.federal]: -1, [COMPANY.nexo]: 0.5, [COMPANY.pampa]: 0, [COMPANY.litoral]: 2,
};

/** What a company pays on its own when no agreement is loaded on the platform. */
export function companyBaseRate(companyOid: string, line: LineOfBusiness): number {
  return LINE_RATE[line] + (COMPANY_ADJUST[companyOid] ?? 0);
}

export function inForce(item: { validFrom: string; validTo: string | null }, date: string): boolean {
  return item.validFrom <= date && (item.validTo === null || date <= item.validTo);
}

export function agreementAt(
  agreements: CommissionAgreement[], producerOid: string, companyOid: string, line: LineOfBusiness, date: string,
): CommissionAgreement | undefined {
  return agreements.find(a => a.producerOid === producerOid && a.companyOid === companyOid
    && a.lineOfBusiness === line && inForce(a, date));
}

export function shareAt(
  splits: CommissionSplit[], parentOid: string, memberOid: string, line: LineOfBusiness, date: string,
): CommissionSplit | undefined {
  return splits.find(s => s.parentOid === parentOid && s.memberOid === memberOid
    && s.lineOfBusiness === line && inForce(s, date));
}

export function expectedOf(collectedPremium: number, ratePct: number | null | undefined): number | null {
  return ratePct === null || ratePct === undefined ? null : Math.round((collectedPremium * ratePct) / 100);
}

export function lineExpected(agreements: CommissionAgreement[], line: CommissionLine): number | null {
  return expectedOf(
    line.collectedPremium,
    agreementAt(agreements, line.holderOid, line.companyOid, line.lineOfBusiness, line.paidDate)?.ratePct,
  );
}

export function lineCompanyPays(agreements: CommissionAgreement[], line: CommissionLine): number {
  const rate = agreementAt(agreements, line.holderOid, line.companyOid, line.lineOfBusiness, line.paidDate)?.ratePct
    ?? companyBaseRate(line.companyOid, line.lineOfBusiness);
  return Math.round((line.collectedPremium * rate) / 100);
}

/** Nearest producer at or above `oid`: the one whose licence and agreements apply to what `oid` sells. */
export function holderOf(members: TreeMember[], oid: string): string {
  let current = members.find(m => m.oid === oid);
  while (current && current.kind !== 'producer' && current.parentOid) {
    current = members.find(m => m.oid === current?.parentOid);
  }
  return current?.oid ?? oid;
}

export function descendantsOf(members: TreeMember[], rootOid: string): TreeMember[] {
  const found = new Set([rootOid]);
  const out: TreeMember[] = [];
  let grew = true;
  while (grew) {
    grew = false;
    for (const m of members) {
      if (m.parentOid && found.has(m.parentOid) && !found.has(m.oid)) {
        found.add(m.oid);
        out.push(m);
        grew = true;
      }
    }
  }
  return out;
}

export function subtreeOids(members: TreeMember[], rootOid: string): Set<string> {
  return new Set([rootOid, ...descendantsOf(members, rootOid).map(m => m.oid)]);
}

/** Suspension reaches the whole subtree, so a node is effectively suspended when it or any ancestor is. */
export function effectiveStatus(members: TreeMember[], oid: string): { status: MemberStatus; byOid: string | null } {
  let current = members.find(m => m.oid === oid);
  while (current) {
    if (current.status !== 'active') return { status: current.status, byOid: current.oid };
    current = current.parentOid ? members.find(m => m.oid === current?.parentOid) : undefined;
  }
  return { status: 'active', byOid: null };
}

export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/** The month before `date`, as `YYYY-MM`. */
export function previousPeriod(date: string): string {
  return monthOf(addDays(`${monthOf(date)}-01`, -1));
}

export function nextPeriodStart(period: string): string {
  return addDays(`${period}-28`, 5).slice(0, 8) + '01';
}

export function periodsBack(today: string, count: number): string[] {
  const out: string[] = [];
  let period = monthOf(today);
  for (let i = 0; i <= count; i++) {
    out.push(period);
    period = previousPeriod(`${period}-01`);
  }
  return out;
}

export function hashOf(text: string): number {
  let h = 0;
  for (const ch of text) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}
